import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ChevronRight, ChevronDown, FolderTree, Check, X } from "lucide-react";

// Fetch the discovered AD OU tree once.
export function useOuTree() {
  const [data, setData] = useState({ flat: [], tree: [], count: 0, discovery: {} });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/ous").then((r) => setData(r.data || { flat: [], tree: [], count: 0 }))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { ...data, loading };
}

// Flatten the nested tree into ordered rows, honouring the collapsed set.
function flatten(tree, collapsed) {
  const rows = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      const kids = n.children || [];
      const isOpen = !collapsed.has(n.distinguishedName);
      rows.push({ dn: n.distinguishedName, name: n.name, depth, hasKids: kids.length > 0, open: isOpen });
      if (kids.length && isOpen) walk(kids, depth + 1);
    }
  };
  walk(tree || [], 0);
  return rows;
}

function TreeRows({ tree, selected, onToggle, multi }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const rows = useMemo(() => flatten(tree, collapsed), [tree, collapsed]);
  const toggleCollapse = (dn) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(dn)) next.delete(dn); else next.add(dn);
    return next;
  });
  return (
    <>
      {rows.map((r) => {
        const isSel = multi ? selected.includes(r.dn) : selected === r.dn;
        return (
          <div key={r.dn} className="flex items-center gap-1 py-1 hover:bg-zinc-50" style={{ paddingLeft: r.depth * 14 }}>
            {r.hasKids ? (
              <button type="button" onClick={() => toggleCollapse(r.dn)} className="text-zinc-400 hover:text-zinc-700" data-testid={`ou-expand-${r.dn}`}>
                {r.open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : <span className="inline-block w-[14px]" />}
            {multi ? (
              <label className="flex flex-1 cursor-pointer items-center gap-2 text-sm text-zinc-700">
                <Checkbox checked={isSel} onCheckedChange={() => onToggle(r.dn)} data-testid={`ou-check-${r.dn}`} />
                <FolderTree size={13} className="text-zinc-400" /> {r.name}
              </label>
            ) : (
              <button type="button" onClick={() => onToggle(r.dn)} data-testid={`ou-pick-${r.dn}`}
                className={`flex flex-1 items-center gap-2 text-left text-sm ${isSel ? "font-semibold text-primary" : "text-zinc-700 hover:text-zinc-900"}`}>
                <FolderTree size={13} className="text-zinc-400" /> {r.name}
                {isSel && <Check size={13} className="text-primary" />}
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

// Inline tree with checkboxes for selecting multiple OUs (e.g. reconciliation scope).
export function OuTreeMulti({ value = [], onChange, tree, count, testid = "ou-tree" }) {
  const toggle = (dn) => onChange(value.includes(dn) ? value.filter((x) => x !== dn) : [...value, dn]);
  return (
    <div className="rounded-sm border border-zinc-200" data-testid={testid}>
      {count === 0 ? (
        <p className="p-3 text-xs text-zinc-400">No OUs discovered yet — the on-prem agent populates these on its next discovery cycle.</p>
      ) : (
        <div className="max-h-64 overflow-auto p-1">
          <TreeRows tree={tree} selected={value} onToggle={toggle} multi />
        </div>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 border-t border-zinc-100 p-2">
          {value.map((dn) => (
            <span key={dn} className="flex items-center gap-1 bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-600">
              {dn}<button type="button" onClick={() => toggle(dn)} data-testid={`ou-chip-remove-${dn}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Single-OU picker: a popover with the tree + a free-text DN fallback.
export function OuSelect({ value = "", onChange, tree, count, placeholder = "Select an OU…", testid = "ou-select" }) {
  const [open, setOpen] = useState(false);
  const pick = (dn) => { onChange(dn); setOpen(false); };
  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testid} className="w-full justify-start rounded-sm border-zinc-300 font-mono text-xs">
            <FolderTree size={13} className="mr-2 shrink-0 text-zinc-400" />
            <span className="truncate">{value || placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[380px] p-0" align="start">
          {count === 0 ? (
            <p className="p-3 text-xs text-zinc-400">No OUs discovered yet — type a DN below or wait for the agent's discovery cycle.</p>
          ) : (
            <div className="max-h-64 overflow-auto p-1">
              <TreeRows tree={tree} selected={value} onToggle={pick} multi={false} />
            </div>
          )}
          <div className="border-t border-zinc-100 p-2">
            <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="OU=…,DC=…" data-testid={`${testid}-freetext`} className="rounded-sm border-zinc-300 font-mono text-xs" />
          </div>
        </PopoverContent>
      </Popover>
      {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`} className="text-zinc-400 hover:text-red-600"><X size={15} /></button>}
    </div>
  );
}
